import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { AppTextService } from '../app-text';

export interface IPasswordValidationProgressDisplayData {
    getPasswordValidationLabelClass: { [key: string]: boolean };
    getPasswordValidationIconName: { [key: string]: boolean };
    isPasswordSet: boolean;
    label: string;
}

@Injectable()
export class FiPasswordValidationProgressHelperService {
    constructor(private appTextService: AppTextService) {}

    getPasswordStrengthValidationDisplayData(
        passwordModelValue: string,
        errors: ValidationErrors,
    ): IPasswordValidationProgressDisplayData {
        return {
            getPasswordValidationLabelClass: this.getPasswordStrengthValidationLabelClass.bind(
                this,
                errors,
                passwordModelValue,
            ),
            getPasswordValidationIconName: this.getPasswordStrengthValidationIconName.bind(
                this,
                errors,
                passwordModelValue,
            ),
            isPasswordSet: this.isPasswordSet.bind(this, passwordModelValue),
            label: this.appTextService.get('password_validate_strength'),
        };
    }

    getPasswordMinLengthValidationDisplayData(
        passwordModelValue: string,
        errors: ValidationErrors,
    ): IPasswordValidationProgressDisplayData {
        return {
            getPasswordValidationLabelClass: this.getPasswordMinLengthValidationLabelClass.bind(
                this,
                errors,
                passwordModelValue,
            ),
            getPasswordValidationIconName: this.getPasswordMinLengthValidationIconName.bind(
                this,
                errors,
                passwordModelValue,
            ),
            isPasswordSet: this.isPasswordSet.bind(this, passwordModelValue),
            label: this.appTextService.get('password_validate_minlength'),
        };
    }

    getConfirmPasswordValidationDisplayData(
        confirmPasswordModelValue: string,
        errors: ValidationErrors,
    ): IPasswordValidationProgressDisplayData {
        return {
            getPasswordValidationLabelClass: this.getConfirmPasswordEqualsValidationLabelClass.bind(
                this,
                errors,
                confirmPasswordModelValue,
            ),
            getPasswordValidationIconName: this.getConfirmPasswordEqualsValidationIconName.bind(this, errors),
            isPasswordSet: this.isPasswordSet.bind(this, confirmPasswordModelValue),
            label: this.appTextService.get('password_validate_match'),
        };
    }

    private isPasswordSet(modelValue: string): boolean {
        return !!modelValue;
    }

    private getPasswordStrengthValidationLabelClass(
        errors: ValidationErrors,
        modelValue: string,
    ): { [key: string]: boolean } {
        return {
            [this.labelValidClassName]: this.isPasswordStrengthValid(errors, modelValue),
            [this.labelInvalidClassName]:
                this.isPasswordSet(modelValue) && this.passwordIncludesError(this.passwordStrengthErrorName, errors),
        };
    }

    private getPasswordStrengthValidationIconName(
        errors: ValidationErrors,
        modelValue: string,
    ): { [key: string]: boolean } {
        return {
            [this.iconValidIconName]: this.isPasswordStrengthValid(errors, modelValue),
            [this.iconInvalidIconName]:
                !this.isPasswordSet(modelValue) || this.passwordIncludesError(this.passwordStrengthErrorName, errors),
        };
    }

    private getPasswordMinLengthValidationLabelClass(
        errors: ValidationErrors,
        modelValue: string,
    ): { [key: string]: boolean } {
        return {
            [this.labelValidClassName]: this.isPasswordMinLengthValid(errors, modelValue),
            [this.labelInvalidClassName]:
                this.isPasswordSet(modelValue) && this.passwordIncludesError(this.passwordMinLengthErrorName, errors),
        };
    }

    private getPasswordMinLengthValidationIconName(
        errors: ValidationErrors,
        modelValue: string,
    ): { [key: string]: boolean } {
        return {
            [this.iconValidIconName]: this.isPasswordMinLengthValid(errors, modelValue),
            [this.iconInvalidIconName]:
                !this.isPasswordSet(modelValue) || this.passwordIncludesError(this.passwordMinLengthErrorName, errors),
        };
    }

    private getConfirmPasswordEqualsValidationLabelClass(
        errors: ValidationErrors,
        modelValue: string,
    ): { [key: string]: boolean } {
        return {
            [this.labelValidClassName]: this.isPasswordSet(modelValue) && !this.isConfirmPasswordNotEqual(errors),
            [this.labelInvalidClassName]: this.isPasswordSet(modelValue) && this.isConfirmPasswordNotEqual(errors),
        };
    }

    private getConfirmPasswordEqualsValidationIconName(errors: ValidationErrors): { [key: string]: boolean } {
        return {
            [this.iconValidIconName]: !this.isConfirmPasswordNotEqual(errors),
            [this.iconInvalidIconName]: this.isConfirmPasswordNotEqual(errors),
        };
    }

    private isConfirmPasswordNotEqual(errors: ValidationErrors): boolean {
        return errors?.equalTo;
    }

    private isPasswordMinLengthValid(errors: ValidationErrors, modelValue: string): boolean {
        return !this.passwordIncludesError(this.passwordMinLengthErrorName, errors) && this.isPasswordSet(modelValue);
    }

    private isPasswordStrengthValid(errors: ValidationErrors, modelValue: string): boolean {
        return this.isPasswordSet(modelValue) && !this.passwordIncludesError(this.passwordStrengthErrorName, errors);
    }

    private passwordIncludesError(errorPropName: string, errors: ValidationErrors): boolean {
        return errors?.hasOwnProperty(errorPropName) && !!errors[errorPropName];
    }

    private readonly passwordStrengthErrorName: string = 'passwordStrength';
    private readonly passwordMinLengthErrorName: string = 'minlength';

    private readonly labelValidClassName: string = 'valid';
    private readonly labelInvalidClassName: string = 'invalid';

    private readonly iconValidIconName: string = 'fa-check';
    private readonly iconInvalidIconName: string = 'fa-times';
}
